import { useCallback, useRef, useState } from 'react';
import { useStore } from '../store/store';
import useEnrichmentAccess from './useEnrichmentAccess';
import { shouldOpenEnrichmentDownloadDialog } from '../components/profiler/CompanyDetails/utils/enrichmentContactsDownload';

/**
 * Owns the "Download contact list as XLSX" decision + dialog state for the two
 * views that offer it: the saved-project view and search results (which exports
 * against its auto-saved project). Both had an identical copy of the enrichment
 * access hook, the open/close state and the branch below; the branch in
 * particular now carries the demo rule (TD-2652), and a rule duplicated per view
 * is a rule the next placement forgets.
 *
 * Call this AFTER useBulkExportConfirm in the consuming component — it needs that
 * hook's `requestBulkExport` for the legacy path.
 *
 * @param {Object} params
 * @param {Function} params.requestBulkExport - useBulkExportConfirm().requestBulkExport
 * @param {Function} [params.onDialogOpen] - Called when the enrichment dialog opens,
 *   and only then. Hosts pass their popup's close, so the anchored desktop popup
 *   does not stay open behind the dialog. On mobile the popup is a MobileSheet in
 *   the browser top layer, which would cover the dialog (a custom modal, not a
 *   native <dialog>) and leave it inert; the sheet already closes on any entry
 *   (`onSelect` on the popup body), so there this is a second, harmless close.
 * @returns {{ isOpen: boolean, close: Function, requestDownload: Function }}
 */
const useProjectContactsDownload = ({ requestBulkExport, onDialogOpen }) => {
  const { profilerAccessStore } = useStore();
  const enrichmentAccess = useEnrichmentAccess();
  const [isOpen, setIsOpen] = useState(false);
  // Read at click time, so an inline callback from the host does not give
  // requestDownload a new identity on every render.
  const onDialogOpenRef = useRef(onDialogOpen);
  onDialogOpenRef.current = onDialogOpen;

  // Enrichment users get the v3 XLSX dialog; everyone else — including demo
  // accounts, whose enriched exports the BE rejects outright — takes the legacy
  // immediate download.
  const requestDownload = useCallback(() => {
    if (
      shouldOpenEnrichmentDownloadDialog('contacts', enrichmentAccess.enabled, {
        isDemo: profilerAccessStore.isProfilerDemo
      })
    ) {
      onDialogOpenRef.current?.();
      setIsOpen(true);
    } else {
      requestBulkExport('contact');
    }
  }, [enrichmentAccess.enabled, profilerAccessStore, requestBulkExport]);

  const close = useCallback(() => setIsOpen(false), []);

  return { isOpen, close, requestDownload };
};

export default useProjectContactsDownload;
